import { ResponsiveContainer } from '@/components/layout/responsive-container';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useSettings } from '@/hooks/use-settings';
import { SUPPORTED_LOCALES } from '@/i18n/i18n';
import { cn } from '@/lib/utils';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ChevronDown,
  ChevronUp,
  FileText,
  GripVertical,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

const templateSectionSchema = z.object({
  title: z.string().min(1, 'templates:validation.sectionTitleRequired'),
  prompt: z.string().min(1, 'templates:validation.sectionInstructionsRequired'),
});

export const templateFormSchema = z.object({
  name: z.string().min(1, 'templates:validation.templateNameRequired'),
  language: z.string().min(2, 'templates:validation.languageRequired'),
  sections: z
    .array(templateSectionSchema)
    .min(1, 'templates:validation.atLeastOneSectionRequired'),
});

export type TemplateFormValues = z.infer<typeof templateFormSchema>;

interface SortableSectionItemProps {
  id: string;
  index: number;
  form: ReturnType<typeof useForm<TemplateFormValues>>;
  onRemove: () => void;
  t: (key: string) => string;
  isExpanded: boolean;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

const SortableSectionItem = ({
  id,
  index,
  form,
  onRemove,
  t,
  isExpanded,
  onToggle,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: SortableSectionItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const titleValue = form.watch(`sections.${index}.title`);
  const promptValue = form.watch(`sections.${index}.prompt`);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('rounded-lg border bg-gray-100 p-4', {
        'cursor-pointer': !isExpanded,
      })}>
      <div className="flex flex-col gap-2">
        <div className="relative flex items-stretch">
          {/* Drag handle - only visible when collapsed */}
          <div
            className={cn('relative -ml-4 pl-2.5 transition-all', {
              'ml-0 w-0 overflow-hidden pl-0': isExpanded,
              'w-10': !isExpanded,
            })}>
            <button
              type="button"
              className="h-full cursor-grab touch-none active:cursor-grabbing"
              {...attributes}
              {...listeners}>
              <GripVertical className="size-5 text-gray-400" />
            </button>
          </div>
          <div className="flex-1 space-y-3">
            {isExpanded ? (
              <>
                {/* Expanded view - full form */}
                {/* Section Title */}
                <FormField
                  control={form.control}
                  name={`sections.${index}.title`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-gray-600">
                        {t('templates:sectionTitle')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('templates:sectionTitlePlaceholder')}
                          className="bg-white shadow-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Section Prompt/Instructions */}
                <FormField
                  control={form.control}
                  name={`sections.${index}.prompt`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-medium text-gray-600">
                        {t('templates:sectionInstructions')}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t(
                            'templates:sectionInstructionsPlaceholder',
                          )}
                          className="bg-white shadow-none"
                          rows={4}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Section Actions */}
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onToggle}
                    className="cursor-pointer">
                    <ChevronUp className="size-4" />
                    {t('templates:collapse')}
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={onMoveUp}
                      disabled={!canMoveUp}>
                      <ChevronUp className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={onMoveDown}
                      disabled={!canMoveDown}>
                      <ChevronDown className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={onRemove}>
                      <Trash2 className="size-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Collapsed view - preview only */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={onToggle}
                      className="flex-1 cursor-pointer text-left">
                      <div className="truncate text-sm font-medium text-gray-900">
                        {titleValue || t('templates:sectionTitlePlaceholder')}
                      </div>
                      <div className="mt-1 line-clamp-2 text-xs text-gray-500">
                        {promptValue ||
                          t('templates:sectionInstructionsPlaceholder')}
                      </div>
                    </button>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={onToggle}>
                        <ChevronDown className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={onRemove}>
                        <Trash2 className="size-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface TemplateFormProps {
  defaultValues?: TemplateFormValues;
  onSubmit: (values: TemplateFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const TemplateForm = ({
  defaultValues,
  onSubmit,
  isSubmitting,
}: TemplateFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useSettings();

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: defaultValues || {
      name: '',
      language: language,
      sections: [],
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'sections',
  });

  // Track expanded state for each section
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [hasInitialized, setHasInitialized] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const prevFieldsLengthRef = useRef(fields.length);

  const addSection = () => {
    append({ title: '', prompt: '' });
  };

  // Expand newly added sections and initialize expanded state for defaultValues
  useEffect(() => {
    if (fields.length > prevFieldsLengthRef.current) {
      // A new section was added
      const newField = fields[fields.length - 1];
      setExpandedSections((prev) => new Set(prev).add(newField.id));
    } else if (
      !hasInitialized &&
      defaultValues?.sections &&
      fields.length > 0 &&
      fields.length === defaultValues.sections.length
    ) {
      // Initialize: expand all sections when loading from defaultValues
      setExpandedSections(new Set(fields.map((field) => field.id)));
      setHasInitialized(true);
    }
    prevFieldsLengthRef.current = fields.length;
  }, [fields, defaultValues, hasInitialized]);

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAllSections = () => {
    const hasAnyExpanded = fields.some((field) =>
      expandedSections.has(field.id),
    );
    if (hasAnyExpanded) {
      // Collapse all
      setExpandedSections(new Set());
    } else {
      // Expand all
      setExpandedSections(new Set(fields.map((field) => field.id)));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex min-h-0 w-full grow flex-col">
        {/* Sections */}
        <ResponsiveContainer className="flex min-h-0 grow flex-col space-y-4 overflow-x-hidden overflow-y-auto pb-3 md:pb-4">
          <div className="flex flex-col gap-3 rounded-lg border bg-gray-100 p-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-gray-600">
                    {t('templates:templateName')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="bg-white shadow-none"
                      placeholder={t('templates:templateNamePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-gray-600">
                    {t('templates:templateLanguage')}
                  </FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                      }}>
                      <SelectTrigger className="bg-white text-base shadow-none">
                        <SelectValue
                          placeholder={t('settings:changeLanguage')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORTED_LOCALES.map((locale) => (
                          <SelectItem key={locale} value={locale}>
                            {t(`home:languages.${locale}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            name="sections"
            render={() => {
              return (
                <FormItem>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-1 items-center justify-between gap-2">
                      <FormLabel className="text-sm font-medium text-gray-700">
                        {t('templates:sections')}
                      </FormLabel>
                      {fields.length > 0 &&
                        (() => {
                          const hasAnyExpanded = fields.some((field) =>
                            expandedSections.has(field.id),
                          );
                          return (
                            <Button
                              type="button"
                              onClick={toggleAllSections}
                              variant="ghost"
                              className="opacity-50"
                              size="sm">
                              {hasAnyExpanded ? (
                                <>
                                  <ChevronUp className="mr-1 size-3.5" />
                                  {t('templates:collapseAll')}
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="mr-1 size-3.5" />
                                  {t('templates:expandAll')}
                                </>
                              )}
                            </Button>
                          );
                        })()}
                    </div>
                    <Button
                      type="button"
                      onClick={addSection}
                      variant="outline"
                      size="sm">
                      <Plus className="size-4" />
                      {t('templates:addSection')}
                    </Button>
                  </div>
                  <FormControl>
                    {fields.length === 0 ? (
                      <div className="rounded-lg border border-dashed p-8 text-center">
                        <FileText className="mx-auto size-8 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">
                          {t('templates:noSections')}
                        </p>
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}>
                        <SortableContext
                          items={fields.map((field) => field.id)}
                          strategy={verticalListSortingStrategy}>
                          <div className="space-y-4">
                            {fields.map((field, index) => (
                              <SortableSectionItem
                                key={field.id}
                                id={field.id}
                                index={index}
                                form={form}
                                onRemove={() => remove(index)}
                                t={t}
                                isExpanded={expandedSections.has(field.id)}
                                onToggle={() => toggleSection(field.id)}
                                onMoveUp={() => {
                                  if (index > 0) {
                                    move(index, index - 1);
                                  }
                                }}
                                onMoveDown={() => {
                                  if (index < fields.length - 1) {
                                    move(index, index + 1);
                                  }
                                }}
                                canMoveUp={index > 0}
                                canMoveDown={index < fields.length - 1}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </ResponsiveContainer>
        <ResponsiveContainer className="w-full">
          <div className="flex gap-2 border-t pt-3 md:py-4">
            <Button
              type="button"
              onClick={() => navigate('/templates')}
              variant="outline"
              disabled={isSubmitting}
              className="flex-1">
              <X className="size-4" />
              {t('templates:cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              className="flex-1">
              <Save className="size-4" />
              {t('templates:save')}
            </Button>
          </div>
        </ResponsiveContainer>
      </form>
    </Form>
  );
};
